import React, { useEffect, useRef } from 'react';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate } from '@fortawesome/pro-regular-svg-icons/faArrowsRotate';
import { faCloudSlash } from '@fortawesome/pro-regular-svg-icons/faCloudSlash';
import { toHubOffer } from 'shared/panda/messages/hub-offer';
import type { AssistantMessageLike } from 'shared/panda/messages/doc-answer';
import { AG_UI_ROLE } from 'shared/panda/agent/ag-ui.constants';
import styles from './render-messages.module.scss';
import { usePandaConversation } from '../../panda-chat-provider';
import { ChatMessage } from './chat-message';
import { EmptyState } from '../empty-state/empty-state';

// Renders the conversation straight from `agent.messages` — one ChatMessage per
// entry. No per-turn view-model; the AG-UI client owns the message list.
export function RenderMessages() {
  const { messages, error, reconnecting, lang, onRetry } = usePandaConversation();
  const latestMessagesRef = useRef<HTMLDivElement>(null);

  // On send, scroll the new question to the top and stay there while the
  // answer streams in below. The wrapper's min-height (scss) guarantees there
  // is enough room to scroll that far.
  const lastQuestionIndex = messages.findLastIndex(message => message.role === AG_UI_ROLE.USER);
  const lastQuestionId = messages[lastQuestionIndex]?.id;
  useEffect(() => {
    if (!lastQuestionId) return;
    latestMessagesRef.current?.scrollIntoView({ block: 'start' });
  }, [lastQuestionId]);

  if (messages.length === 0) {
    return (
      <div className={classNames(styles.root, 'comprehensive-text')}>
        <EmptyState />
      </div>
    );
  }

  const previousMessages = lastQuestionIndex >= 0 ? messages.slice(0, lastQuestionIndex) : messages;
  const latestMessages = lastQuestionIndex >= 0 ? messages.slice(lastQuestionIndex) : [];
  const lastMessage = messages[messages.length - 1];

  // Ids of messages the next message follows with an ASA offer — the local
  // refusal of a fallback offer. Its rating row is suppressed (that refusal is
  // part of the offer, not a standalone answer).
  const offerPrecededIds = new Set(
    messages
      .filter((_, index) => {
        const next = messages[index + 1];
        return Boolean(next && toHubOffer(next as AssistantMessageLike));
      })
      .map(message => message.id),
  );

  return (
    <div className={classNames(styles.root, 'comprehensive-text')}>
      {previousMessages.map(message => (
        <ChatMessage
          key={message.id}
          message={message}
          isLast={false}
          precedesOffer={offerPrecededIds.has(message.id)}
        />
      ))}
      {latestMessages.length > 0 && (
        <div ref={latestMessagesRef} className={styles.latestMessages}>
          {latestMessages.map(message => (
            <ChatMessage
              key={message.id}
              message={message}
              isLast={message === lastMessage}
              precedesOffer={offerPrecededIds.has(message.id)}
            />
          ))}
          {reconnecting && <p className={styles.reconnectingText}>{lang.RECONNECTING_MESSAGE}</p>}
          {error === 'connection' ? (
            <div className={styles.connectionLostBar}>
              <div className={styles.connectionLostMessage}>
                <FontAwesomeIcon icon={faCloudSlash} className={styles.connectionLostIcon} />
                <span>{`${lang.CONNECTION_LOST_TITLE} — ${lang.CONNECTION_LOST_SUBTITLE}`}</span>
              </div>
              <button type="button" className={styles.retryButton} onClick={onRetry}>
                {lang.RETRY}
                <FontAwesomeIcon icon={faArrowsRotate} />
              </button>
            </div>
          ) : (
            error && <p className={styles.errorText}>{lang.ERROR_MESSAGE}</p>
          )}
        </div>
      )}
    </div>
  );
}
